import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import {
  Phone, Mail, MapPin, Calendar, Trash2, LogOut, Loader2, Inbox, FileText,
  CheckCircle2, Clock, Send, Pencil, Plus, X, Printer, Share2, Users,
  CalendarDays, ListChecks, Megaphone,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";
import SharePanel from "./SharePanel";
import CustomersPanel from "./CustomersPanel";
import JobsCalendar from "./JobsCalendar";
import JobLog from "./JobLog";

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "#FF3B30" },
  { value: "contacted", label: "Contacted", color: "#FF9500" },
  { value: "scheduled", label: "Scheduled", color: "#FFCC00" },
  { value: "converted", label: "Converted → Job", color: "#0A84FF" },
  { value: "completed", label: "Completed", color: "#34C759" },
  { value: "archived", label: "Archived", color: "#71717A" },
];

// Default to "tomorrow at 10am" in the local timezone for the datetime-local input.
function defaultSlot() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("bookings");

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--te-bg)" }}>
      <header className="border-b border-white/10 sticky top-0 z-30" style={{ background: "rgba(5,5,5,0.85)", backdropFilter: "blur(20px)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center" style={{ background: "var(--te-primary)" }}>
              <span className="text-xs font-bold">T</span>
            </div>
            <div className="font-bold text-sm tracking-tight">TECH EMERGENCY · ADMIN</div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-xs text-zinc-400 hidden sm:block">{user.email}</span>
            <Link
              to="/admin/flyers"
              data-testid="admin-flyers-link"
              className="te-btn te-btn-ghost text-sm"
            >
              <Printer size={14} /> <span className="hidden sm:inline">Flyers</span>
            </Link>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="te-btn te-btn-ghost text-sm"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Stats />
        <div className="flex gap-2 mt-8 mb-6 border-b border-white/10">
          {[
            { id: "bookings", label: "Bookings", Icon: Inbox },
            { id: "adleads", label: "Ad Leads", Icon: Megaphone },
            { id: "calendar", label: "Calendar", Icon: CalendarDays },
            { id: "joblog", label: "Job Log", Icon: ListChecks },
            { id: "customers", label: "Customers", Icon: Users },
            { id: "blog", label: "Blog Manager", Icon: FileText },
            { id: "share", label: "Share to Social", Icon: Share2 },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id ? "text-white" : "text-zinc-500 hover:text-zinc-200"
              }`}
              style={{ borderBottomColor: tab === t.id ? "var(--te-primary)" : "transparent" }}
            >
              <t.Icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "bookings" && <BookingsPanel />}
        {tab === "adleads" && <BookingsPanel adLeads />}
        {tab === "calendar" && <JobsCalendar />}
        {tab === "joblog" && <JobLog />}
        {tab === "customers" && <CustomersPanel />}
        {tab === "blog" && <BlogPanel />}
        {tab === "share" && <SharePanel />}
      </div>
    </div>
  );
}

function Stats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  if (!stats) return null;
  const items = [
    { label: "New bookings", value: stats.bookings.new, accent: true },
    { label: "Ad leads", value: stats.bookings.ad_leads ?? 0 },
    { label: "Scheduled", value: stats.bookings.scheduled },
    { label: "Completed", value: stats.bookings.completed },
    { label: "Total bookings", value: stats.bookings.total },
    { label: "Blog posts (published)", value: stats.blog.published },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-px" style={{ background: "rgba(255,255,255,0.08)" }} data-testid="admin-stats">
      {items.map((i) => (
        <div key={i.label} className="p-5" style={{ background: "var(--te-bg)" }}>
          <div className="te-overline text-[10px] mb-2">{i.label}</div>
          <div className="text-3xl font-bold tracking-tight" style={{ color: i.accent ? "var(--te-primary)" : "var(--te-text)" }}>{i.value}</div>
        </div>
      ))}
    </div>
  );
}

function BookingsPanel({ adLeads = false }) {
  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/admin/bookings").then((r) => setAllBookings(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const bookings = useMemo(
    () => allBookings.filter((b) => (adLeads ? b.source === "Adelaide Enquiry" : b.source !== "Adelaide Enquiry")),
    [allBookings, adLeads]
  );

  const filtered = useMemo(() => {
    if (filter === "all") return bookings;
    return bookings.filter((b) => b.status === filter);
  }, [bookings, filter]);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    setAllBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
  };
  const updateNotes = async (id, notes) => {
    await api.patch(`/admin/bookings/${id}`, { notes });
    setAllBookings((bs) => bs.map((b) => (b.id === id ? { ...b, notes } : b)));
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    await api.delete(`/admin/bookings/${id}`);
    setAllBookings((bs) => bs.filter((b) => b.id !== id));
  };

  if (loading) return <div className="te-overline">Loading…</div>;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <button
          onClick={() => setFilter("all")}
          data-testid="filter-all"
          className={`px-3 py-1.5 text-xs te-overline border ${filter === "all" ? "border-[var(--te-primary)] text-white" : "border-white/10 text-zinc-400"}`}
        >All ({bookings.length})</button>
        {STATUS_OPTIONS.map((o) => {
          const count = bookings.filter((b) => b.status === o.value).length;
          return (
            <button
              key={o.value}
              data-testid={`filter-${o.value}`}
              onClick={() => setFilter(o.value)}
              className={`px-3 py-1.5 text-xs te-overline border ${filter === o.value ? "border-[var(--te-primary)] text-white" : "border-white/10 text-zinc-400"}`}
            >{o.label} ({count})</button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="te-card p-12 text-center">
          <Inbox className="mx-auto mb-3 text-zinc-600" />
          <p className="text-zinc-400">{adLeads ? "No ad leads yet. Leads from /adelaide-enquiry will appear here." : "No bookings match this filter."}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((b) => (
            <BookingCard key={b.id} booking={b} onStatusChange={updateStatus} onNotesChange={updateNotes} onDelete={remove} />
          ))}
        </div>
      )}
    </div>
  );
}

function BookingCard({ booking, onStatusChange, onNotesChange, onDelete }) {
  const [notesDraft, setNotesDraft] = useState(booking.notes || "");
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleErr, setScheduleErr] = useState("");
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [schedule, setSchedule] = useState({
    scheduled_at: defaultSlot(),
    duration_minutes: 60,
  });
  const status = STATUS_OPTIONS.find((s) => s.value === booking.status) || STATUS_OPTIONS[0];

  const submitSchedule = async (e) => {
    e.preventDefault();
    setScheduleSaving(true);
    setScheduleErr("");
    try {
      await api.post(`/admin/bookings/${booking.id}/convert-to-job`, {
        scheduled_at: new Date(schedule.scheduled_at).toISOString(),
        duration_minutes: Number(schedule.duration_minutes),
        status: "scheduled",
      });
      setShowSchedule(false);
      onStatusChange(booking.id, "scheduled");
    } catch (err) {
      setScheduleErr(formatApiError(err?.response?.data?.detail) || "Could not convert to job.");
    } finally {
      setScheduleSaving(false);
    }
  };

  return (
    <div className="te-card p-5 md:p-6" data-testid={`booking-row-${booking.id}`}>
      <div className="grid md:grid-cols-4 gap-4">
        <div className="md:col-span-1">
          <div className="te-overline text-[10px] mb-1 flex items-center gap-2">
            <Calendar size={11}/> {new Date(booking.created_at).toLocaleString("en-AU")}
          </div>
          <div className="font-semibold text-lg tracking-tight">{booking.name}</div>
          <a href={`tel:${booking.phone}`} className="text-sm text-zinc-300 flex items-center gap-1 hover:text-[var(--te-primary)] mt-1"><Phone size={12}/> {booking.phone}</a>
          {booking.email && <a href={`mailto:${booking.email}`} className="text-sm text-zinc-400 flex items-center gap-1 hover:text-white mt-1"><Mail size={12}/> {booking.email}</a>}
          <div className="text-sm text-zinc-400 flex items-center gap-1 mt-1"><MapPin size={12}/> {booking.suburb}</div>
        </div>

        <div className="md:col-span-2">
          <div className="te-overline text-[10px] mb-1">{booking.service}</div>
          <div className="text-sm mb-2">
            <span className="te-overline text-[10px] mr-2">URGENCY</span>
            <span className="text-zinc-300">{booking.urgency}</span>
            {booking.preferred_contact && <>
              <span className="te-overline text-[10px] ml-3 mr-2">PREFERS</span>
              <span className="text-zinc-300 capitalize">{booking.preferred_contact}</span>
            </>}
            {booking.preferred_time && <>
              <span className="te-overline text-[10px] ml-3 mr-2">WHEN</span>
              <span className="text-zinc-300">{booking.preferred_time}</span>
            </>}
          </div>
          {booking.message && <p className="text-sm text-zinc-400 leading-relaxed">{booking.message}</p>}
          {booking.attribution && Object.keys(booking.attribution).length > 0 && (
            <div className="text-xs text-zinc-400 mt-2 break-all" data-testid={`booking-attribution-${booking.id}`}>
              Ad data: {Object.entries(booking.attribution).map(([k, v]) => `${k}=${v}`).join(" · ")}
            </div>
          )}
          <textarea
            data-testid={`booking-notes-${booking.id}`}
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            onBlur={() => notesDraft !== (booking.notes || "") && onNotesChange(booking.id, notesDraft)}
            placeholder="Internal notes…"
            rows={2}
            className="te-input mt-3 text-sm"
          />
        </div>

        <div className="md:col-span-1 flex flex-col gap-2">
          <select
            data-testid={`booking-status-${booking.id}`}
            value={booking.status}
            onChange={(e) => onStatusChange(booking.id, e.target.value)}
            className="te-input"
            style={{ borderColor: status.color }}
          >
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {booking.email && booking.status !== "converted" && (
            <button
              onClick={() => setShowSchedule((v) => !v)}
              data-testid={`booking-schedule-${booking.id}`}
              className="te-btn te-btn-primary text-sm"
            >
              <CalendarDays size={14}/> Schedule job
            </button>
          )}
          <button
            onClick={() => onDelete(booking.id)}
            data-testid={`booking-delete-${booking.id}`}
            className="te-btn te-btn-ghost text-sm"
          >
            <Trash2 size={14}/> Delete
          </button>
        </div>
      </div>

      {showSchedule && (
        <form onSubmit={submitSchedule} className="mt-4 border-t border-white/10 pt-4 grid sm:grid-cols-3 gap-3" data-testid={`booking-schedule-form-${booking.id}`}>
          <label className="block">
            <div className="te-overline text-[10px] mb-2">Date &amp; time</div>
            <input
              type="datetime-local"
              required
              value={schedule.scheduled_at}
              onChange={(e) => setSchedule((s) => ({...s, scheduled_at: e.target.value}))}
              className="te-input"
              data-testid={`booking-schedule-when-${booking.id}`}
            />
          </label>
          <label className="block">
            <div className="te-overline text-[10px] mb-2">Duration (min)</div>
            <input
              type="number" min={15} max={480} step={15}
              required
              value={schedule.duration_minutes}
              onChange={(e) => setSchedule((s) => ({...s, duration_minutes: e.target.value}))}
              className="te-input"
              data-testid={`booking-schedule-duration-${booking.id}`}
            />
          </label>
          <div className="flex items-end gap-2">
            <button
              type="submit"
              disabled={scheduleSaving}
              className="te-btn te-btn-primary text-sm"
              data-testid={`booking-schedule-submit-${booking.id}`}
            >
              {scheduleSaving ? "Scheduling…" : "Convert → Job"}
            </button>
            <button
              type="button"
              onClick={() => setShowSchedule(false)}
              className="te-btn te-btn-ghost text-sm"
            >Cancel</button>
          </div>
          {scheduleErr && <div className="text-sm sm:col-span-3" style={{ color: "var(--te-primary)" }}>{scheduleErr}</div>}
        </form>
      )}

      <style>{`.te-input { width:100%; background: var(--te-surface); border:1px solid var(--te-border); color:var(--te-text); padding:0.6rem 0.75rem; font-family:'IBM Plex Sans'; outline:none;} .te-input:focus { border-color: var(--te-primary);}`}</style>
    </div>
  );
}

function BlogPanel() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} new | post obj

  const load = () => {
    setLoading(true);
    api.get("/admin/blog").then((r) => setPosts(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/${id}`);
    setPosts((ps) => ps.filter((p) => p.id !== id));
  };

  if (loading) return <div className="te-overline">Loading…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold tracking-tight">Posts ({posts.length})</h2>
        <button
          data-testid="new-blog-post-btn"
          onClick={() => setEditing({ title: "", excerpt: "", content: "", cover_image: "", tags: [], published: true })}
          className="te-btn te-btn-primary"
        >
          <Plus size={14}/> New post
        </button>
      </div>

      {editing && <BlogEditor post={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="te-card p-5 flex items-start justify-between gap-4" data-testid={`blog-row-${p.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <h3 className="font-semibold text-lg tracking-tight truncate">{p.title}</h3>
                <span className="te-overline text-[10px]" style={{ color: p.published ? "var(--te-success)" : "var(--te-text-muted)" }}>
                  {p.published ? "PUBLISHED" : "DRAFT"}
                </span>
              </div>
              <p className="text-sm text-zinc-400 line-clamp-2">{p.excerpt}</p>
              <div className="text-xs text-zinc-600 mt-2">/blog/{p.slug}</div>
            </div>
            <div className="flex flex-col gap-2 flex-shrink-0">
              <button onClick={() => setEditing(p)} data-testid={`edit-post-${p.id}`} className="te-btn te-btn-ghost text-sm"><Pencil size={12}/> Edit</button>
              <button onClick={() => remove(p.id)} data-testid={`delete-post-${p.id}`} className="te-btn te-btn-ghost text-sm"><Trash2 size={12}/> Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BlogEditor({ post, onClose, onSaved }) {
  const isEdit = Boolean(post.id);
  const [form, setForm] = useState({
    title: post.title || "",
    excerpt: post.excerpt || "",
    content: post.content || "",
    cover_image: post.cover_image || "",
    tags: (post.tags || []).join(", "),
    published: post.published !== false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (isEdit) {
        await api.patch(`/admin/blog/${post.id}`, payload);
      } else {
        await api.post(`/admin/blog`, payload);
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }}>
      <form onSubmit={save} data-testid="blog-editor" className="w-full max-w-3xl max-h-[90vh] overflow-y-auto te-card p-6 md:p-8 space-y-4" style={{ background: "var(--te-paper)" }}>
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold tracking-tight">{isEdit ? "Edit post" : "New post"}</h3>
          <button type="button" onClick={onClose} data-testid="blog-editor-close" className="text-zinc-400 hover:text-white"><X size={20}/></button>
        </div>

        <Field label="Title" required>
          <input data-testid="post-title" required value={form.title} onChange={(e) => setForm((f) => ({...f, title: e.target.value}))} className="te-input" />
        </Field>
        <Field label="Excerpt">
          <textarea data-testid="post-excerpt" rows={2} value={form.excerpt} onChange={(e) => setForm((f) => ({...f, excerpt: e.target.value}))} className="te-input"/>
        </Field>
        <Field label="Cover image URL">
          <input data-testid="post-cover" value={form.cover_image} onChange={(e) => setForm((f) => ({...f, cover_image: e.target.value}))} className="te-input"/>
        </Field>
        <Field label="Tags (comma separated)">
          <input data-testid="post-tags" value={form.tags} onChange={(e) => setForm((f) => ({...f, tags: e.target.value}))} className="te-input"/>
        </Field>
        <Field label="Content (Markdown: ## h2, ### h3, **bold**, - list)" required>
          <textarea data-testid="post-content" required rows={12} value={form.content} onChange={(e) => setForm((f) => ({...f, content: e.target.value}))} className="te-input font-mono text-sm"/>
        </Field>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            data-testid="post-published"
            checked={form.published}
            onChange={(e) => setForm((f) => ({...f, published: e.target.checked}))}
          /> Published
        </label>

        {error && <div className="text-sm" style={{ color: "var(--te-primary)" }}>{error}</div>}

        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={saving} data-testid="post-save-btn" className="te-btn te-btn-primary disabled:opacity-50">
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Create post")}
          </button>
          <button type="button" onClick={onClose} className="te-btn te-btn-ghost">Cancel</button>
        </div>

        <style>{`.te-input { width:100%; background: var(--te-surface); border:1px solid var(--te-border); color:var(--te-text); padding:0.6rem 0.75rem; font-family:'IBM Plex Sans'; outline:none;} .te-input:focus { border-color: var(--te-primary);}`}</style>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="te-overline text-[10px] mb-2">
        {label}{required && <span style={{ color: "var(--te-primary)" }}> *</span>}
      </div>
      {children}
    </label>
  );
}
